import { useEffect, useRef, useState } from "react";
import { useArenaStore } from "../../stores/useArenaStore";
import type { ArenaState } from "@shared/types";

interface KillFeedProps {
  state: ArenaState;
}

interface FeedItem {
  id: string;
  text: string;
  color: string;
}

export function KillFeed({ state }: KillFeedProps) {
  const { playerId } = useArenaStore();
  const [items, setItems] = useState<FeedItem[]>([]);
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const fresh: FeedItem[] = [];
    for (const ev of state.events) {
      if (seenRef.current.has(ev.id)) continue;
      seenRef.current.add(ev.id);
      if (seenRef.current.size > 200) {
        seenRef.current = new Set([...seenRef.current].slice(-100));
      }

      if (ev.kind === "kill") {
        const d = ev.data as Record<string, string>;
        // creature-vs-creature kills carry no names; skip them (noise)
        if (d.creatureVsCreature) continue;
        const isPlayerKill = d.victimKind === "player";
        const byMe = d.killerId === playerId;
        const killer = d.killerName || "??";
        const victim = d.victimName || "??";
        fresh.push({
          id: ev.id,
          text: byMe
            ? isPlayerKill
              ? `💀 You eliminated ${victim}`
              : `🏹 You slew a ${victim}`
            : isPlayerKill
              ? `💀 ${killer} eliminated ${victim}`
              : `🏹 ${killer} slew a ${victim}`,
          color: byMe
            ? "text-dawn-400 font-bold"
            : isPlayerKill
              ? "text-red-400"
              : "text-archer-300",
        });
      } else if (ev.kind === "levelup") {
        const d = ev.data as Record<string, string | number>;
        fresh.push({
          id: ev.id,
          text: `⬆️ ${d.name} reached Lv.${d.level}`,
          color: "text-dawn-400",
        });
      } else if (ev.kind === "respawn") {
        const d = ev.data as Record<string, string>;
        fresh.push({
          id: ev.id,
          text: `✨ ${d.name} returns to battle`,
          color: "text-green-400",
        });
      } else if (ev.kind === "join") {
        const d = ev.data as Record<string, string>;
        if (d.name) {
          fresh.push({
            id: ev.id,
            text: `🏹 ${d.name} joined the ${d.faction === "dawn" ? "Dawn" : "Dusk"}`,
            color: d.faction === "dawn" ? "text-dawn-400" : "text-dusk-400",
          });
        }
      }
    }
    if (fresh.length > 0) {
      setItems((prev) => [...fresh.reverse(), ...prev].slice(0, 6));
    }
  }, [state.events]);

  // auto-expire items
  useEffect(() => {
    if (items.length === 0) return;
    const id = setTimeout(() => {
      setItems((prev) => prev.slice(0, -1));
    }, 4000);
    return () => clearTimeout(id);
  }, [items]);

  if (items.length === 0) return null;

  return (
    <div className="pointer-events-none absolute right-4 top-16 flex w-64 flex-col gap-1">
      {items.map((item) => (
        <div
          key={item.id}
          className={`animate-float-up rounded-lg border border-archer-700/60 bg-archer-900/90 px-3 py-1.5 text-xs backdrop-blur ${item.color}`}
        >
          {item.text}
        </div>
      ))}
    </div>
  );
}
